import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: 12/29/24</p>

        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-600">NumisNavigator ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our coin collection management service.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">2.1 Personal Information</h3>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Account information (email address, password)</li>
                  <li>Profile information</li>
                  <li>Client information (for business users)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">2.2 Coin Collection Data</h3>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>Coin details and descriptions</li>
                  <li>Images of coins</li>
                  <li>Transaction records</li>
                  <li>Certification and grading information</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">2.3 Technical Data</h3>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>IP address</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Usage data and analytics</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <ul className="list-disc pl-5 text-gray-600">
              <li>To provide and maintain our service</li>
              <li>To process and manage coin certification submissions</li>
              <li>To communicate with you about service updates and notifications</li>
              <li>To improve our service and user experience</li>
              <li>To protect against fraud and unauthorized access</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data Sharing Practices</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.1 Our Commitment</h3>
                <p className="text-gray-600">We do not sell, rent, trade, or otherwise share your personal information with third parties for their marketing or commercial purposes. Your privacy and trust are fundamental to our service.</p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">4.2 Limited Data Sharing</h3>
                <p className="text-gray-600">We only share your information in the following limited circumstances:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>When explicitly requested by you for certification services</li>
                  <li>To comply with legal obligations or valid legal processes</li>
                  <li>To protect our rights, privacy, safety, or property</li>
                  <li>In connection with a business transfer or acquisition (you would be notified)</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Third-Party Services</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.1 Certification Services</h3>
                <p className="text-gray-600">We integrate with third-party certification services (NGC, PCGS) to provide grading and certification services. When you use these features, your coin information may be shared with these services according to their respective privacy policies.</p>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">5.2 PCGS API Integration</h3>
                <p className="text-gray-600 mb-2">For PCGS API integration:</p>
                <ul className="list-disc pl-5 text-gray-600">
                  <li>We securely store your PCGS API key in encrypted form</li>
                  <li>Your API key is only used for authorized PCGS service requests</li>
                  <li>We do not share your API key with any third parties</li>
                  <li>You can remove your API key from our system at any time</li>
                  <li>We maintain audit logs of all API requests made using your key</li>
                </ul>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
            <p className="text-gray-600 mb-4">We implement appropriate technical and organizational security measures to protect your information, including:</p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Encryption of sensitive data</li>
              <li>Secure file storage for coin images</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
            <p className="text-gray-600 mb-4">You have the right to:</p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Export your data</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-600">We retain your information for as long as your account is active or as needed to provide services. We may retain certain data as required by law or for legitimate business purposes.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
            <p className="text-gray-600">We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Us</h2>
            <p className="text-gray-600">If you have questions about this Privacy Policy, please contact us at hello@numisnavigator.com.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
